import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, DollarSign, CreditCard, AlertCircle, Settings, Send } from 'lucide-react';
import { InvoiceSettingsService, InvoiceSettings, defaultInvoiceSettings } from '../services/invoiceSettingsService';
import { useBusiness } from '../contexts/BusinessContext';
import RichTextDescription from './RichTextDescription';
import { voidPayment } from '../lib/payments';
import SendInvoiceModal from './SendInvoiceModal';
import { sendGmailEmail } from '../lib/email';
import { supabase } from '../lib/supabaseClient';
import { ProductPhotoService } from '../services/productPhotoService';

// Custom portal-based toast component for modal contexts
const PortalToast = ({ 
  children, 
  type, 
  onClose 
}: { 
  children: React.ReactNode; 
  type: 'loading' | 'success' | 'error';
  onClose: () => void;
}) => {
  const getIcon = () => {
    switch (type) {
      case 'loading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>;
      case 'success':
        return <div className="rounded-full h-4 w-4 bg-green-500 flex items-center justify-center text-white text-xs">✓</div>;
      case 'error':
        return <div className="rounded-full h-4 w-4 bg-red-500 flex items-center justify-center text-white text-xs">✕</div>;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'loading':
        return 'border-blue-200';
      case 'success':
        return 'border-green-200';
      case 'error':
        return 'border-red-200';
    }
  };

  return createPortal(
    <div 
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border ${getBorderColor()} p-4 flex items-center space-x-3 min-w-[300px] max-w-md`}
      style={{ zIndex: 999999 }}
    >
      {getIcon()}
      <div className="flex-1">
        {children}
      </div>
      {type !== 'loading' && (
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-2"
        >
          ×
        </button>
      )}
    </div>,
    document.body
  );
};

// Custom toast function with portal rendering for modal contexts
const createPortalToast = (
  setShowPortalToast: (show: boolean) => void,
  setPortalToastMessage: (message: React.ReactNode) => void,
  setPortalToastType: (type: 'loading' | 'success' | 'error') => void
) => ({
  loading: (message: React.ReactNode, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('loading');
    setShowPortalToast(true);
    return 'portal-toast-loading';
  },
  success: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('success');
    setShowPortalToast(true);
    // Auto-hide success toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-success';
  },
  error: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('error');
    setShowPortalToast(true);
    // Auto-hide error toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-error';
  },
  dismiss: (toastId?: string) => {
    setShowPortalToast(false);
  }
});

interface BetaPaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  bookingNumber: number;
  bookingData: {
    total_amount: number;
    customer?: {
      id: string;
      first_name: string;
      last_name: string;
      email?: string;
      phone?: string;
      full_name?: string;
    };
    payments?: Array<{
      id: string;
      amount: number;
      tip_amount?: number;
      base_amount?: number;
      status: string;
      pdf_url?: string;
      created_at: string;
      reference?: string;
      stripe_payment_intent_id?: string;
      stripe_checkout_session_id?: string;
      notes?: string;
    }>;
  };
  booking?: any; // Add full booking object for services access
  onUpdate?: () => void | Promise<void>;
  isInvisible?: boolean;
}

export default function BetaPaymentsModal({ 
  isOpen, 
  onClose, 
  bookingId, 
  bookingNumber,
  bookingData,
  booking,
  onUpdate,
  isInvisible = false
}: BetaPaymentsModalProps) {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [voidMessage, setVoidMessage] = useState('');
  const [selectedPaymentToVoid, setSelectedPaymentToVoid] = useState<any>(null);
  const [isVoidingPayment, setIsVoidingPayment] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [bookingProducts, setBookingProducts] = useState<any[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const [showPortalToast, setShowPortalToast] = useState(false);
  const [portalToastMessage, setPortalToastMessage] = useState<React.ReactNode>(null);
  const [portalToastType, setPortalToastType] = useState<'loading' | 'success' | 'error'>('loading');

  // Create portal toast instance
  const portalToast = createPortalToast(setShowPortalToast, setPortalToastMessage, setPortalToastType);


  // Load booking products
  const loadBookingProducts = async () => {
    if (!bookingId) return;

    try {
      const { data, error } = await supabase
        .from('product_booking_sales')
        .select('*')
        .eq('booking_id', bookingId);

      if (error) {
        console.error('Error loading booking products:', error);
        return;
      }

      setBookingProducts(data || []);

      // Load product photos if we have products
      if (data && data.length > 0) {
        const stockItemIds = data.map(p => p.stock_item_id);
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && typeof photo === 'object' && 'photo_url' in photo) {
                photoUrls[itemId] = (photo as any).photo_url;
              }
            });
          }
          setProductPhotos(photoUrls);
        } catch (photoError) {
          console.error('Error loading product photos:', photoError);
          setProductPhotos({});
        }
      }
    } catch (error) {
      console.error('Error loading booking products:', error);
    }
  };
  
  // Debug logging
  useEffect(() => {
    console.log('BetaPaymentsModal - isSendInvoiceModalOpen state changed:', isSendInvoiceModalOpen);
  }, [isSendInvoiceModalOpen]);




  // Calculate payment status with tip breakdown
  const totalAmount = bookingData.total_amount || 0;
  const completedPayments = bookingData.payments?.filter(p => p.status === 'completed') || [];
  
  // Calculate base payments (excluding tips)
  const totalPaid = completedPayments.reduce((sum, payment) => sum + (payment.base_amount || payment.amount || 0), 0);
  
  // Calculate total tips received
  const totalTips = completedPayments.reduce((sum, payment) => sum + (payment.tip_amount || 0), 0);
  
  // Calculate total received (base payments + tips)
  const totalReceived = totalPaid + totalTips;
  
  const balanceOwing = Math.max(0, totalAmount - totalPaid);
  
  const paymentStatus = balanceOwing === 0 ? 'paid' : totalPaid > 0 ? 'partial' : 'unpaid';







  // Fetch invoice settings when modal opens
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      if (!isOpen || !selectedBusiness?.id) return;
      
      setIsLoadingSettings(true);
      try {
        const settings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
        if (settings) {
          setInvoiceSettings(settings);
        } else {
          // No settings found, create default settings with business info
          const defaultSettings: InvoiceSettings = {
            business_id: selectedBusiness.id,
            company_name: selectedBusiness.name || 'Your Business Name',
            company_address: selectedBusiness.address || '123 Business Street\nCity, State 12345',
            company_phone: selectedBusiness.phone || '(555) 123-4567',
            company_email: selectedBusiness.email || 'info@yourbusiness.com',
            company_website: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
            ...defaultInvoiceSettings
          } as InvoiceSettings;
          setInvoiceSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error fetching invoice settings:', error);
        // Fall back to defaults
        const fallbackSettings: InvoiceSettings = {
          business_id: selectedBusiness.id,
          company_name: selectedBusiness.name || 'Your Business Name',
          company_address: selectedBusiness.address || '123 Business Street\nCity, State 12345',
          company_phone: selectedBusiness.phone || '(555) 123-4567',
          company_email: selectedBusiness.email || 'info@yourbusiness.com',
          company_website: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
          ...defaultInvoiceSettings
        } as InvoiceSettings;
        setInvoiceSettings(fallbackSettings);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchInvoiceSettings();
  }, [isOpen, selectedBusiness?.id]);

  // Re-render when booking data changes to keep invoice in sync
  useEffect(() => {
    // This effect just forces a re-render when booking changes
    // All the invoice data is already using live booking values
  }, [booking]);

  // Handle voiding the most recent payment
  const handleVoidPayment = () => {
    if (!booking?.payments || booking.payments.length === 0) return;
    
    const payments = booking.payments;
    const validPayments = payments.filter((payment: any) => 
      payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
    );
    
    if (validPayments.length === 0) return;
    
    // Get the most recent valid payment
    const mostRecentPayment = validPayments[validPayments.length - 1];
    
    // Set the payment to void and open the modal
    setSelectedPaymentToVoid(mostRecentPayment);
    setVoidMessage('');
    setIsVoidModalOpen(true);
  };

  // Confirm the void payment with message
  const confirmVoidPayment = async () => {
    if (!selectedPaymentToVoid) return;
    
    try {
      setIsVoidingPayment(true);
      
      console.log('Voiding payment:', {
        paymentId: selectedPaymentToVoid.id,
        amount: selectedPaymentToVoid.amount,
        voidMessage: voidMessage
      });
      
      // Actually void the payment
      await voidPayment(selectedPaymentToVoid.id, voidMessage);
      
      console.log('Payment voided successfully');
      
      // Close the modal
      setIsVoidModalOpen(false);
      setSelectedPaymentToVoid(null);
      setVoidMessage('');
      
      // After successful void, call onUpdate to refresh the booking data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error voiding payment:', error);
      alert('Failed to void payment. Please try again.');
    } finally {
      setIsVoidingPayment(false);
    }
  };

  // Generate invoice PDF and return as blob
  const generateInvoicePDF = async (): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      // Import libraries dynamically
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      // Find the invoice preview element
      const invoiceElement = document.querySelector('.invoice-preview') as HTMLElement;
      if (!invoiceElement) {
        alert('Invoice preview not found. Please try again.');
        return null;
      }

      // Convert to canvas
      const canvas = await html2canvas(invoiceElement, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename
      const invoiceNumber = booking?.booking_number || bookingNumber;
      const filename = `invoice-${invoiceNumber}.pdf`;
      
      // Get PDF as blob
      const blob = pdf.output('blob');
      
      return { blob, filename };
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      return null;
    }
  };

  // Download invoice as PDF
  const downloadInvoicePDF = async () => {
    if (isDownloadingPDF) return;
    
    try {
      setIsDownloadingPDF(true);
      
      const pdfData = await generateInvoicePDF();
      if (!pdfData) return;
      
      // Create download link
      const url = URL.createObjectURL(pdfData.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  // Handle sending invoice via email with optional payment link
  const handleSendInvoiceEmail = async (allowStripePay: boolean = true) => {
    if (!bookingData.customer?.email || !selectedBusiness) {
      portalToast.error('Customer email not found');
      return;
    }

    try {
      setIsSendingEmail(true);
      
      // Show immediate sending feedback with loading - same as payment summary
      const loadingToast = portalToast.loading(
        <div>
          <div>Sending invoice...</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close booking</div>
        </div>
      );
      
      // Process everything in background without blocking UI
      processInvoiceInBackground(allowStripePay, loadingToast);
      
    } catch (error) {
      console.error('Error initiating invoice send:', error);
      portalToast.error('Failed to send invoice. Please try again.');
      setIsSendingEmail(false);
    }
  };

  // Background processing function for heavy operations
  const processInvoiceInBackground = async (allowStripePay: boolean, loadingToast?: string) => {
    try {
      // Generate PDF
      const pdfData = await generateInvoicePDF();
      if (!pdfData) return;
      
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;
      
      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length,
        contentPreview: base64Content.substring(0, 50) + '...'
      });

      // Generate payment link if balance is owed and Stripe Connect is enabled
      let paymentLinkHtml = '';
      
      const currentTotal = booking?.total_amount || bookingData.total_amount || 0;
      const payments = booking?.payments || bookingData.payments || [];
      const validPayments = payments.filter((payment: any) => 
        payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
      );
      const totalPaid = validPayments.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0);
      const amountOwing = Math.max(0, currentTotal - totalPaid);

      console.log('🔍 Payment link check:', {
        amountOwing,
        hasStripeAccount: !!selectedBusiness?.stripe_connect_account_id,
        onlinePaymentsEnabled: selectedBusiness?.online_payments_enabled,
        stripeConnectAccountId: selectedBusiness?.stripe_connect_account_id
      });

      if (amountOwing > 0 && allowStripePay && selectedBusiness?.stripe_connect_account_id && selectedBusiness?.online_payments_enabled !== false) {
        try {
          console.log('🔗 Generating payment link for invoice...');
          
          // Smart payment record management - prevent duplicates and reuse links
          let unpaidPayment = payments.find((p: any) => 
            p.status === 'pending' && 
            (p.amount === 0 || p.reference === 'unpaid-invoice')
          );
          
          let paymentUrl = null;
          
          if (!unpaidPayment) {
            console.log('🔍 No existing pending payment found, creating new one...');
            // Create unpaid invoice record
            const { data: newPayment, error: paymentError } = await supabase
              .from('payments')
              .insert({
                business_id: selectedBusiness?.id || '',
                booking_id: bookingId,
                amount: 0, // Placeholder for unpaid invoice
                method: 'credit_card',
                status: 'pending',
                reference: 'unpaid-invoice',
                notes: `Unpaid invoice for booking #${bookingNumber}`,
                payment_token: crypto.randomUUID()
              })
              .select()
              .single();

            if (paymentError) {
              console.error('Failed to create unpaid payment record:', paymentError);
            } else {
              unpaidPayment = newPayment;
              console.log('✅ Created new unpaid payment record:', unpaidPayment.id);
            }
          } else {
            console.log('✅ Found existing pending payment record:', unpaidPayment.id);
          }

          if (unpaidPayment) {
            // Check if this payment already has a payment link
            const { data: existingLink, error: linkCheckError } = await supabase
              .from('payment_links')
              .select('payment_url')
              .eq('payment_id', unpaidPayment.id)
              .eq('status', 'active')
              .single();

            if (!linkCheckError && existingLink?.payment_url) {
              // Reuse existing payment link
              paymentUrl = existingLink.payment_url;
              console.log('♻️ Reusing existing payment link:', paymentUrl);
            } else {
              // Generate new payment link
              console.log('🔗 Generating new payment link...');
              const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
                body: { paymentId: unpaidPayment.id }
              });

              if (!linkError && linkData?.paymentUrl) {
                paymentUrl = linkData.paymentUrl;
                console.log('✅ New payment link generated:', paymentUrl);
              } else {
                console.error('❌ Failed to generate payment link:', linkError);
              }
            }

            if (paymentUrl) {
              
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${amountOwing.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
            }
          }
        } catch (paymentLinkError) {
          console.error('Error generating payment link:', paymentLinkError);
          // Continue without payment link
        }
      }
      
      // Prepare email content
      const customerName = bookingData.customer.full_name || 
        `${bookingData.customer.first_name} ${bookingData.customer.last_name}`.trim();
      
      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">Invoice #${bookingNumber}</h1>
            <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px;">from ${selectedBusiness.name}</p>
          </div>
          
          <div style="padding: 30px;">
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Hi ${customerName},</p>
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Please find attached your invoice for booking #${bookingNumber}.</p>
            
            ${paymentLinkHtml}
            
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea;">
              <h4 style="color: #374151; margin: 0 0 10px 0; font-size: 16px;">Invoice Summary</h4>
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280;">Total Amount:</span>
                <span style="color: #374151; font-weight: bold;">$${currentTotal.toFixed(2)}</span>
              </div>
              ${totalPaid > 0 ? `
                <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                  <span style="color: #6b7280;">Amount Paid:</span>
                  <span style="color: #059669; font-weight: bold;">$${totalPaid.toFixed(2)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin: 8px 0; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                  <span style="color: #374151; font-weight: bold;">Amount Due:</span>
                  <span style="color: ${amountOwing > 0 ? '#dc2626' : '#059669'}; font-weight: bold;">$${amountOwing.toFixed(2)}</span>
                </div>
              ` : ''}
            </div>
            
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 20px 0;">Thank you for your business!</p>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
              <p style="color: #374151; font-size: 16px; margin: 0;">Best regards,<br><strong>${selectedBusiness?.name || 'Business'}</strong></p>
            </div>
          </div>
          
          <div style="background: #f8fafc; padding: 20px; text-align: center; border-radius: 0 0 12px 12px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px; margin: 0;">This is an automated invoice from ${selectedBusiness?.name || 'Business'}</p>
          </div>
        </div>
      `;
      
      // Send email with attachment
      const result = await sendGmailEmail({
        to: bookingData.customer?.email || '',
        subject: `Invoice #${bookingNumber} from ${selectedBusiness?.name || 'Business'}${amountOwing > 0 ? ' - Pay Online' : ' - Thank You'}`,
        body: emailBody,
        businessId: selectedBusiness?.id || '',
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });
      
      if (result.success) {
        console.log('✅ Invoice sent successfully in background!');
        if (loadingToast) {
          portalToast.dismiss(loadingToast);
        }
        portalToast.success('Invoice sent successfully! ✅');
        
        // Refresh the booking data to show updated invoice sent status
        if (onUpdate) {
          try {
            // Call onUpdate to refresh the booking data
            const result = onUpdate();
            // Handle both sync and async onUpdate functions
            if (result instanceof Promise) {
              await result;
            }
            console.log('✅ Booking data refreshed after invoice send');
          } catch (error) {
            console.error('❌ Failed to refresh booking data:', error);
          }
        }
      } else {
        console.error('❌ Failed to send invoice in background:', result.error);
        if (loadingToast) {
          portalToast.dismiss(loadingToast);
        }
        portalToast.error('Failed to send invoice. Please try again.');
      }
      
    } catch (error) {
      console.error('❌ Error in background invoice processing:', error);
      if (loadingToast) {
        portalToast.dismiss(loadingToast);
      }
      portalToast.error('Failed to send invoice. Please try again.');
    }
  };

  // Handle send method selection
  const handleSendMethodSelect = (method: 'email' | 'sms', allowStripePay: boolean) => {
    console.log('BetaPaymentsModal - Method selected:', method, 'Allow Stripe Pay:', allowStripePay);
    if (method === 'email') {
      handleSendInvoiceEmail(allowStripePay);
    } else {
      // SMS functionality coming soon
      alert('SMS functionality coming soon!');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      loadBookingProducts(); // Load products when modal opens
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isInvisible ? 'opacity-0 pointer-events-none' : (isOpen ? 'bg-opacity-50' : 'bg-opacity-0')
        } z-[100000]`}
        style={isInvisible ? { 
          position: 'fixed', 
          top: '-9999px', 
          left: '-9999px', 
          zIndex: -1,
          opacity: 0,
          pointerEvents: 'none'
        } : {}}
        onClick={isInvisible ? undefined : onClose}
        onTransitionEnd={handleTransitionEnd}
      >
      <div 
        className={`fixed inset-0 bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isInvisible ? 'translate-y-0' : (isOpen ? 'translate-y-0' : 'translate-y-full')
        }`}
        style={isInvisible ? { 
          position: 'fixed', 
          top: '-9999px', 
          left: '-9999px', 
          zIndex: -1,
          width: '100vw',
          height: '100vh',
          opacity: 0,
          pointerEvents: 'none'
        } : {}}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-screen flex flex-col">
          {/* Header - Clean Modern Design */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    Payments (Beta)
                  </h2>
                  <span className="text-sm text-gray-300">Booking #{bookingNumber}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                  BETA
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="space-y-0">
              
              {/* Payment Status Section */}
              <div className="bg-white border-b border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 space-y-2 sm:space-y-0">
                  <h3 className="text-lg font-semibold text-gray-900">Payment Status</h3>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    paymentStatus === 'paid' 
                      ? 'bg-green-100 text-green-800' 
                      : paymentStatus === 'partial' 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {paymentStatus === 'paid' && <CreditCard className="h-4 w-4 mr-1" />}
                    {paymentStatus === 'partial' && <AlertCircle className="h-4 w-4 mr-1" />}
                    {paymentStatus === 'unpaid' && <DollarSign className="h-4 w-4 mr-1" />}
                    {paymentStatus === 'paid' ? 'Paid in Full' : paymentStatus === 'partial' ? 'Partially Paid' : 'Unpaid'}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {/* Total Amount */}
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      ${totalAmount.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">Total Amount</div>
                  </div>

                  {/* Amount Paid */}
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-700">
                      ${totalPaid.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">Amount Paid</div>
                  </div>

                  {/* Tips Received */}
                  {totalTips > 0 && (
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-700">
                        ${totalTips.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">Tips Received</div>
                    </div>
                  )}

                  {/* Balance Owing */}
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-700">
                      ${balanceOwing.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">Balance Owing</div>
                  </div>

                  {/* Invoice Sent Status */}
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-700">
                      {(() => {
                        const payments = booking?.payments || bookingData.payments || [];
                        const hasInvoiceSent = payments.some((p: any) => p.payment_token || p.stripe_checkout_session_id);
                        return hasInvoiceSent ? '✓' : '✗';
                      })()}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {(() => {
                        const payments = booking?.payments || bookingData.payments || [];
                        const hasInvoiceSent = payments.some((p: any) => p.payment_token || p.stripe_checkout_session_id);
                        return hasInvoiceSent ? 'Invoice Sent' : 'Not Sent';
                      })()}
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>Payments Made:</span>
                    <span>{completedPayments.length} payment{completedPayments.length !== 1 ? 's' : ''}</span>
                  </div>
                  {paymentStatus !== 'paid' && (
                    <div className="flex justify-between items-center text-sm text-gray-600 mt-2">
                      <span>Payment Status:</span>
                      <span className="font-medium">
                        {paymentStatus === 'partial' ? 'Payment required' : 'Full payment required'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Payment History Dropdown */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setIsPaymentHistoryOpen(!isPaymentHistoryOpen)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">Payment History</span>
                      <span className="text-xs text-gray-500">({(booking?.payments || bookingData.payments || []).length} entries)</span>
                    </div>
                    <div className={`transform transition-transform duration-200 ${isPaymentHistoryOpen ? 'rotate-180' : ''}`}>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {isPaymentHistoryOpen && (
                    <div className="mt-4 space-y-3">
                      {(() => {
                        const payments = booking?.payments || bookingData.payments || [];
                        

                        
                        // Sort payments by created_at (newest first)
                        const sortedPayments = [...payments].sort((a, b) => 
                          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                        );
                        
                        if (sortedPayments.length === 0) {
                          return (
                            <div className="p-4 rounded-lg border backdrop-blur-sm shadow-sm bg-gray-50 border-gray-200">
                              <div className="text-center text-gray-500">
                                <div className="text-sm font-medium">No payment entries yet</div>
                                <div className="text-xs mt-1">Payment records will appear here when created</div>
                              </div>
                            </div>
                          );
                        }
                        
                        return sortedPayments.map((payment, index) => {

                          
                          // Determine styling based on payment status
                          let statusConfig = {
                            color: 'text-gray-600',
                            bgColor: 'bg-gray-50',
                            borderColor: 'border-gray-200',
                            label: payment.status || 'Unknown',
                            icon: '●'
                          };
                          
                          switch (payment.status) {
                            case 'completed':
                            case 'succeeded':
                            case 'paid':
                              statusConfig = {
                                color: 'text-green-600',
                                bgColor: 'bg-green-50',
                                borderColor: 'border-green-200',
                                label: 'Completed',
                                icon: '✓'
                              };
                              break;
                            case 'pending':
                              statusConfig = {
                                color: 'text-yellow-600',
                                bgColor: 'bg-yellow-50',
                                borderColor: 'border-yellow-200',
                                label: payment.stripe_checkout_session_id ? 'Pending Stripe Pay' : 'Pending',
                                icon: '⏳'
                              };
                              break;
                            case 'failed':
                              statusConfig = {
                                color: 'text-red-600',
                                bgColor: 'bg-red-50',
                                borderColor: 'border-red-200',
                                label: 'Failed',
                                icon: '✗'
                              };
                              break;
                            case 'voided':
                              statusConfig = {
                                color: 'text-gray-500',
                                bgColor: 'bg-gray-100',
                                borderColor: 'border-gray-300',
                                label: 'Voided',
                                icon: '⊘'
                              };
                              break;
                            default:
                              statusConfig = {
                                color: 'text-gray-600',
                                bgColor: 'bg-gray-50',
                                borderColor: 'border-gray-200',
                                label: payment.status || 'Unknown',
                                icon: '●'
                              };
                          }
                          
                          return (
                            <div key={payment.id || index} className={`p-3 rounded-lg border backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-200 ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-3 h-3 rounded-full ${statusConfig.color.replace('text-', 'bg-')} flex-shrink-0 flex items-center justify-center text-xs font-bold text-white`}>
                                    {statusConfig.icon}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className={`font-medium text-sm ${statusConfig.color}`}>
                                      {payment.reference || 'Payment'} - {statusConfig.label}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {new Date(payment.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                      {payment.method && (
                                        <span className="ml-2 text-gray-500">
                                          • {payment.method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </span>
                                      )}
                                    </div>

                                    {payment.stripe_payment_intent_id && (
                                      <div className="text-xs text-blue-600 mt-1 font-medium">
                                        Paid with Stripe
                                      </div>
                                    )}
                                    {payment.notes && (
                                      <div className="text-xs text-gray-400 mt-1">
                                        {payment.notes}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between sm:justify-end space-x-3">
                                  <div className={`font-semibold text-base ${statusConfig.color}`}>
                                    ${(payment.amount || 0).toFixed(2)}
                                  </div>
                                  {/* Void button - only show for completed payments that aren't already voided */}
                                  {payment.status === 'completed' && payment.amount > 0 && (
                                    <button
                                      onClick={() => {
                                        setSelectedPaymentToVoid(payment);
                                        setVoidMessage('');
                                        setIsVoidModalOpen(true);
                                      }}
                                      className="px-2 py-1 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-red-600 border border-white/20 rounded hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md backdrop-blur-sm flex-shrink-0"
                                    >
                                      Void
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Preview Section */}
              <div className="bg-white border-b border-gray-200 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Invoice Preview</h3>
                      <div className="mt-2 sm:mt-3">
                        <button
                          onClick={() => navigate('/time-accounting?tab=invoice-settings')}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Settings className="w-3 h-3" />
                          <span className="hidden sm:inline">Invoice Settings</span>
                          <span className="sm:hidden">Settings</span>
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                      <button
                        onClick={() => {
                          console.log('Send button clicked');
                          console.log('isLoadingSettings:', isLoadingSettings);
                          console.log('isSendingEmail:', isSendingEmail);
                          console.log('Setting isSendInvoiceModalOpen to true');
                          setIsSendInvoiceModalOpen(true);
                        }}
                        disabled={isLoadingSettings || isSendingEmail}
                        className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSendingEmail ? (
                          <div className="w-4 h-4 mr-2 border-2 border-gray-300 border-t-white rounded-full animate-spin"></div>
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        {isSendingEmail ? 'Sending...' : 'Send'}
                      </button>
                      <button
                        onClick={downloadInvoicePDF}
                        disabled={isLoadingSettings || isDownloadingPDF}
                        className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium bg-white text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isDownloadingPDF ? (
                          <div className="w-4 h-4 mr-2 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                        <span className="hidden sm:inline">{isDownloadingPDF ? 'Generating PDF...' : 'Download PDF'}</span>
                        <span className="sm:hidden">{isDownloadingPDF ? 'Generating...' : 'PDF'}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6 lg:p-8">
                  {isLoadingSettings ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <div className="w-12 h-12 border-4 border-gradient-to-r from-blue-500 to-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-6 bg-gradient-to-r from-blue-500 to-purple-600 opacity-20"></div>
                        <p className="text-gray-700 font-medium text-lg">Loading invoice settings...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="invoice-preview bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 relative mx-auto max-w-2xl" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                    
                                        {/* Invoice Header */}
                    <div className="p-4 sm:p-6 lg:p-8" style={{ backgroundColor: invoiceSettings?.header_background || '#F8FAFC' }}>
                      <div className="flex flex-col sm:flex-row justify-between items-start space-y-4 sm:space-y-0">
                        <div className="w-full sm:w-auto">
                          {invoiceSettings?.include_logo && invoiceSettings?.logo_url && (
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg mb-3 sm:mb-4 flex items-center justify-center text-gray-400 text-xs overflow-hidden">
                              <img 
                                src={invoiceSettings.logo_url} 
                                alt="Company Logo" 
                                className="w-full h-full object-contain"
                              />
                            </div>
                          )}
                          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: invoiceSettings?.primary_color || '#3B82F6' }}>
                            INVOICE
                          </h1>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">#{`${invoiceSettings?.invoice_prefix || 'INV-'}${booking?.booking_number || bookingNumber}`}</p>
                        </div>
                        <div className="text-left sm:text-right w-full sm:w-auto">
                          <h2 className="text-lg sm:text-xl font-semibold" style={{ color: invoiceSettings?.text_color || '#374151' }}>
                            {invoiceSettings?.company_name || 'Your Business Name'}
                          </h2>
                          <div className="text-xs sm:text-sm text-gray-600 mt-2 whitespace-pre-line">
                            {invoiceSettings?.company_address || '123 Business Street\nCity, State 12345'}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
                            <div>{invoiceSettings?.company_phone || '(555) 123-4567'}</div>
                            <div className="break-all">{invoiceSettings?.company_email || 'info@yourbusiness.com'}</div>
                            <div className="break-all">{invoiceSettings?.company_website || 'www.yourbusiness.com'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
      
                                                            {/* Invoice Details */}
                    <div className="p-4 sm:p-6 lg:p-8">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
                        {/* Bill To - Mobile Optimized */}
                        <div className="bg-gray-50 sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0">
                          <h3 className="font-semibold text-sm sm:text-base lg:text-lg mb-2 text-gray-800" style={{ color: invoiceSettings?.text_color || '#374151' }}>Bill To:</h3>
                          <div className="text-gray-600 text-sm sm:text-base space-y-1">
                            <div className="font-semibold text-gray-900">
                              {(booking?.customer || bookingData.customer)
                                ? `${(booking?.customer || bookingData.customer).first_name} ${(booking?.customer || bookingData.customer).last_name}`
                                : 'Customer Name'
                              }
                            </div>
                            {(booking?.customer || bookingData.customer)?.email && (
                              <div className="break-all text-xs sm:text-sm">{(booking?.customer || bookingData.customer).email}</div>
                            )}
                            {(booking?.customer || bookingData.customer)?.phone && (
                              <div className="text-xs sm:text-sm">{(booking?.customer || bookingData.customer).phone}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Invoice Info - Mobile Optimized */}
                        <div className="bg-gray-50 sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0 text-left sm:text-right">
                          <div className="space-y-2 text-xs sm:text-sm lg:text-base">
                            <div className="flex justify-between sm:justify-end sm:space-x-4">
                              <span className="font-medium text-gray-700">Invoice Date:</span>
                              <span className="font-semibold">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="flex justify-between sm:justify-end sm:space-x-4">
                              <span className="font-medium text-gray-700">Due Date:</span>
                              <span className="font-semibold">{invoiceSettings?.payment_terms === 'On receipt' 
                                ? new Date().toLocaleDateString()
                                : invoiceSettings?.payment_terms === 'Due within 90 days'
                                ? new Date(Date.now() + 90*24*60*60*1000).toLocaleDateString()
                                : new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()
                              }</span>
                            </div>
                            {invoiceSettings?.show_vehicle && (
                              <div className="flex justify-between sm:justify-end sm:space-x-4">
                                <span className="font-medium text-gray-700">Vehicle:</span>
                                <span className="font-semibold">Vehicle Info</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                          
                      {/* Services Table */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 sm:mb-8">
                        {/* Desktop Table Header */}
                        <div className="hidden sm:block bg-gray-50 p-4">
                          <div className="grid gap-2 font-semibold text-gray-700" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                            <div>Service</div>
                            <div className="text-center">Quantity</div>
                            <div className="text-right">Rate</div>
                            <div className="text-right">Amount</div>
                          </div>
                        </div>
                        {/* Mobile Header */}
                        <div className="sm:hidden bg-gradient-to-r from-gray-100 to-gray-50 p-4 border-b border-gray-200">
                          <div className="font-bold text-gray-800 text-base">Services & Pricing</div>
                        </div>
                                                <div className="divide-y sm:divide-y-0">
                          {/* Actual services from booking */}
                          {booking?.services && booking.services.length > 0 ? (
                            booking.services.map((service: any, index: number) => (
                              <div key={service.id || index} className="p-2 sm:p-4">
                                {/* Desktop Grid Layout */}
                                <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                  <div>
                                    <div className="font-medium">{service.name}</div>
                                    {invoiceSettings?.show_item_descriptions && service.description && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        <RichTextDescription 
                                          description={service.description} 
                                          className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                        />
                                      </div>
                                    )}
                                    {invoiceSettings?.show_vehicle && (service.vehicle_make || service.vehicle_model) && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {service.vehicle_year} {service.vehicle_make} {service.vehicle_model}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-center">1</div>
                                  <div className="text-right">${(service.price || 0).toFixed(2)}</div>
                                  <div className="text-right font-medium">${(1 * (service.price || 0)).toFixed(2)}</div>
                                </div>
                                
                                {/* Mobile Card Layout */}
                                <div className="sm:hidden mb-3">
                                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                    {/* Service Name and Price Row */}
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-base text-gray-900">{service.name}</div>
                                      </div>
                                      <div className="ml-3 text-right">
                                        <div className="font-bold text-lg text-gray-900">${(1 * (service.price || 0)).toFixed(2)}</div>
                                      </div>
                                    </div>
                                    
                                    {/* Description */}
                                    {invoiceSettings?.show_item_descriptions && service.description && (
                                      <div className="text-sm text-gray-600 leading-relaxed">
                                        <RichTextDescription 
                                          description={service.description} 
                                          className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                        />
                                      </div>
                                    )}
                                    
                                    {/* Vehicle Info and Quantity/Rate */}
                                    <div className="flex justify-between items-center pt-1">
                                      <div className="flex-1">
                                        {invoiceSettings?.show_vehicle && (service.vehicle_make || service.vehicle_model) && (
                                          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded inline-block">
                                            {service.vehicle_year} {service.vehicle_make} {service.vehicle_model}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-right text-xs text-gray-500">
                                        Qty: 1 × ${(service.price || 0).toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                                                    ) : (
                            <div className="p-2 sm:p-4">
                              {/* Desktop Grid Layout */}
                              <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                <div>
                                  <div className="font-medium">Booking Service</div>
                                  {invoiceSettings?.show_item_descriptions && (
                                    <div className="text-sm text-gray-600 mt-1">
                                      <RichTextDescription 
                                        description="Professional service" 
                                        className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="text-center">1</div>
                                <div className="text-right">${totalAmount.toFixed(2)}</div>
                                <div className="text-right font-medium">${(1 * totalAmount).toFixed(2)}</div>
                              </div>
                              
                              {/* Mobile Card Layout */}
                              <div className="sm:hidden mb-3">
                                <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                  {/* Service Name and Price Row */}
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-semibold text-base text-gray-900">Booking Service</div>
                                    </div>
                                    <div className="ml-3 text-right">
                                      <div className="font-bold text-lg text-gray-900">${(1 * totalAmount).toFixed(2)}</div>
                                    </div>
                                  </div>
                                  
                                  {/* Description */}
                                  {invoiceSettings?.show_item_descriptions && (
                                    <div className="text-sm text-gray-600 leading-relaxed">
                                      <RichTextDescription 
                                        description="Professional service" 
                                        className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                                      />
                                    </div>
                                  )}
                                  
                                  {/* Quantity/Rate */}
                                  <div className="flex justify-end pt-1">
                                    <div className="text-right text-xs text-gray-500">
                                      Qty: 1 × ${totalAmount.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                    </div>
                      </div>

                      {/* Products Table */}
                      {bookingProducts && bookingProducts.length > 0 && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 sm:mb-8">
                          {/* Desktop Table Header */}
                          <div className="hidden sm:block bg-gray-50 p-4">
                            <div className="grid gap-2 font-semibold text-gray-700" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                              <div>Product</div>
                              <div className="text-center">Quantity</div>
                              <div className="text-right">Rate</div>
                              <div className="text-right">Amount</div>
                            </div>
                          </div>
                          {/* Mobile Header */}
                          <div className="sm:hidden bg-gradient-to-r from-gray-100 to-gray-50 p-4 border-b border-gray-200">
                            <div className="font-bold text-gray-800 text-base">Products & Pricing</div>
                          </div>
                          <div className="divide-y sm:divide-y-0">
                            {bookingProducts.map((product: any, index: number) => (
                              <div key={product.id || index} className="p-2 sm:p-4">
                                {/* Desktop Grid Layout */}
                                <div className="hidden sm:grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                                  <div>
                                    <div className="font-medium">{product.product_name}</div>
                                  </div>
                                  <div className="text-center">{product.quantity}</div>
                                  <div className="text-right">${(product.unit_price || 0).toFixed(2)}</div>
                                  <div className="text-right font-medium">${(product.total_price || 0).toFixed(2)}</div>
                                </div>
                                
                                {/* Mobile Card Layout */}
                                <div className="sm:hidden mb-3">
                                  <div className="bg-gray-50 rounded-lg p-3 space-y-2 shadow-sm">
                                    {/* Product Name and Price Row */}
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-base text-gray-900">{product.product_name}</div>
                                      </div>
                                      <div className="ml-3 text-right">
                                        <div className="font-bold text-lg text-gray-900">${(product.total_price || 0).toFixed(2)}</div>
                                      </div>
                                    </div>
                                    
                                    {/* Quantity/Rate */}
                                    <div className="flex justify-end pt-1">
                                      <div className="text-right text-xs text-gray-500">
                                        Qty: {product.quantity} × ${(product.unit_price || 0).toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                                                                  {/* Totals - Mobile Optimized */}
                      <div className="flex justify-end">
                        <div className="w-full sm:w-80 lg:w-64">
                          <div className="bg-gray-50 sm:bg-transparent rounded-lg p-3 sm:p-0 space-y-2 text-sm sm:text-base">
                            <div className="flex justify-between py-1">
                              <span className="text-gray-700">Subtotal:</span>
                              <span className="font-semibold">${(booking?.subtotal || totalAmount).toFixed(2)}</span>
                            </div>
                            {/* Show discounts if they exist */}
                            {booking?.discount_amount && booking.discount_amount > 0 && (
                              <div className="flex justify-between text-red-600 py-1">
                                <span>Discount:</span>
                                <span className="font-semibold">-${(booking.discount_amount).toFixed(2)}</span>
                              </div>
                            )}
                            {invoiceSettings?.show_tax_breakdown && (
                              <div className="flex justify-between text-gray-600 py-1">
                                <span>Tax:</span>
                                <span className="font-semibold">${(booking?.tax_amount || 0).toFixed(2)}</span>
                              </div>
                            )}
                            <div className="border-t pt-3 mt-3">
                              <div className="flex justify-between font-bold text-lg sm:text-xl bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent sm:text-gray-900" style={{ color: invoiceSettings?.accent_color || '#059669' }}>
                                <span>Total:</span>
                                <span>${(booking?.total_amount || totalAmount).toFixed(2)}</span>
                              </div>
                              
                                                            {/* Payment breakdown with tip display - only show if payments exist */}
                              {(() => {
                                const currentTotal = booking?.total_amount || totalAmount;
                                const payments = booking?.payments || bookingData.payments || [];
                                const validPayments = payments.filter((payment: any) => 
                                  payment.status === 'completed' || payment.status === 'succeeded' || payment.status === 'paid'
                                );
                                
                                // Calculate base payments (excluding tips)
                                const totalPaid = validPayments.reduce((sum: number, payment: any) => sum + (payment.base_amount || payment.amount || 0), 0);
                                
                                // Calculate total tips received
                                const totalTips = validPayments.reduce((sum: number, payment: any) => sum + (payment.tip_amount || 0), 0);
                                
                                // Calculate total received (base payments + tips)
                                const totalReceived = totalPaid + totalTips;
                                
                                const amountOwing = Math.max(0, currentTotal - totalPaid);
                                
                                if (validPayments.length > 0) {
                                  return (
                                    <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 space-y-1">
                                      <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Amount Paid:</span>
                                        <span className="text-green-600 font-medium">${totalPaid.toFixed(2)}</span>
                                      </div>
                                      {totalTips > 0 && (
                                        <div className="flex justify-between text-xs sm:text-sm">
                                          <span className="text-gray-600">Tips Received:</span>
                                          <span className="text-green-600 font-medium">${totalTips.toFixed(2)}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Total Received:</span>
                                        <span className="text-green-600 font-medium">${totalReceived.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between text-xs sm:text-sm">
                                        <span className="text-gray-600">Amount Owing:</span>
                                        <span className={`font-medium ${amountOwing > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                          ${amountOwing.toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>

                                            {/* Footer - Mobile Optimized */}
                      <div className="mt-6 sm:mt-8 lg:mt-12 pt-4 sm:pt-6 lg:pt-8 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-8">
                          <div className="bg-gray-50 sm:bg-transparent rounded-lg p-3 sm:p-0">
                            <h4 className="font-semibold mb-2 text-sm sm:text-base text-gray-800">Payment Terms:</h4>
                            <p className="text-xs sm:text-sm text-gray-600 font-medium">{invoiceSettings?.payment_terms || 'On receipt'}</p>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2 leading-relaxed">{invoiceSettings?.late_fees_policy || '1.5% monthly service charge on overdue amounts'}</p>
                          </div>
                          <div className="text-center sm:text-right bg-gray-50 sm:bg-transparent rounded-lg p-3 sm:p-0 flex items-center justify-center sm:block">
                            <p className="text-sm sm:text-base lg:text-lg font-semibold" style={{ color: invoiceSettings?.accent_color || '#059669' }}>
                              {invoiceSettings?.thank_you_message || 'Thank you for your business!'}
                            </p>
                          </div>
                        </div>
                      </div>
                                             </div>
                     </div>
                   )}
                 </div>
                               </div>

            </div>
          </div>
        </div>
      
        {/* Void Payment Modal */}
        {isVoidModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60000] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Void Payment</h3>
            
                                    {selectedPaymentToVoid && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-600 font-medium">Payment Amount:</div>
                <div className="text-2xl font-bold text-green-600">
                  ${selectedPaymentToVoid.amount?.toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-2 font-mono break-all">
                  Payment ID: {selectedPaymentToVoid.id}
                </div>
              </div>
            )}
              
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3">
                Void Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={voidMessage}
                onChange={(e) => setVoidMessage(e.target.value)}
                placeholder="Enter reason for voiding this payment..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm resize-none"
                rows={3}
              />
            </div>
              
                        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => {
                  setIsVoidModalOpen(false);
                  setSelectedPaymentToVoid(null);
                  setVoidMessage('');
                }}
                disabled={isVoidingPayment}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-bold border border-white/30 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg backdrop-blur-sm ${
                  isVoidingPayment
                    ? 'text-gray-400 bg-gray-100/50 cursor-not-allowed'
                    : 'text-gray-700 bg-white/60 hover:bg-white/80 hover:shadow-xl'
                }`}
              >
                Cancel
              </button>
              <button
                 onClick={confirmVoidPayment}
                 disabled={!voidMessage.trim() || isVoidingPayment}
                 className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-bold border border-white/20 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 shadow-lg backdrop-blur-sm ${
                   voidMessage.trim() && !isVoidingPayment
                     ? 'text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 focus:ring-red-500 hover:shadow-xl'
                     : 'text-gray-400 bg-gray-200/50 cursor-not-allowed'
                 }`}
               >
                 {isVoidingPayment ? 'Voiding...' : 'Void Payment'}
               </button>
            </div>
          </div>
        </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      <SendInvoiceModal
        isOpen={isSendInvoiceModalOpen}
        onClose={() => setIsSendInvoiceModalOpen(false)}
        onSelectMethod={handleSendMethodSelect}
        customerEmail={bookingData.customer?.email}
        customerPhone={bookingData.customer?.phone}
        hasStripeConnected={!!selectedBusiness?.stripe_connect_account_id}
      />

      {/* Portal Toast for Modal Context */}
      {showPortalToast && (
        <PortalToast
          type={portalToastType}
          onClose={() => setShowPortalToast(false)}
        >
          {portalToastMessage}
        </PortalToast>
      )}
    </>
  );
} 